import React, { useState, useEffect } from 'react';
import { Check, X, FileText, Plus, Trash2, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { colors } from '../../constants/theme';
import { registrationsService } from '../../services/api';
import { CATEGORIES, CATEGORY_LABELS, calculateAge, getEligibleCategories } from '../../constants/categories';
import LoadingSpinner from '../Common/LoadingSpinner';

const STATUS_LABELS = {
  pending: 'Pendentes',
  approved: 'Aprovadas',
  rejected: 'Rejeitadas',
};

export default function RegistrationsManager() {
  const [activeTab, setActiveTab] = useState('pending');
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [proofModal, setProofModal] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [listResp, statsResp] = await Promise.all([
        registrationsService.list(activeTab),
        registrationsService.stats(),
      ]);
      if (listResp.success) setRegistrations(listResp.data || []);
      if (statsResp.success) setStats(statsResp.data);
    } catch (e) {
      console.error('Erro ao carregar inscrições:', e);
    } finally {
      setLoading(false);
    }
  };

  const setBusy = (id, busy) =>
    setActionLoading((prev) => ({ ...prev, [id]: busy }));

  const handleApprove = async (reg) => {
    if (!window.confirm(`Aprovar inscrição de ${reg.name} (${reg.category})?`)) return;
    setBusy(reg.id, true);
    try {
      const resp = await registrationsService.approve(reg.id);
      if (resp.success) {
        await loadData();
      }
    } catch (e) {
      alert('Erro ao aprovar: ' + (e.error?.message || 'desconhecido'));
    } finally {
      setBusy(reg.id, false);
    }
  };

  const handleReject = async (reg) => {
    const reason = window.prompt(`Motivo da rejeição (opcional):`, '');
    if (reason === null) return; // cancelado
    setBusy(reg.id, true);
    try {
      const resp = await registrationsService.reject(reg.id, reason);
      if (resp.success) await loadData();
    } catch (e) {
      alert('Erro ao rejeitar: ' + (e.error?.message || 'desconhecido'));
    } finally {
      setBusy(reg.id, false);
    }
  };

  const handleDelete = async (reg) => {
    if (!window.confirm(`Remover inscrição de ${reg.name}? Esta ação não pode ser desfeita.`)) return;
    setBusy(reg.id, true);
    try {
      await registrationsService.remove(reg.id);
      await loadData();
    } catch (e) {
      alert('Erro ao remover: ' + (e.error?.message || 'desconhecido'));
    } finally {
      setBusy(reg.id, false);
    }
  };

  const handleViewProof = async (reg) => {
    try {
      setProofModal({ loading: true, registration: reg });
      const { url, mimetype } = await registrationsService.getPaymentProofBlobUrl(reg.id);
      setProofModal({ loading: false, registration: reg, url, mimetype });
    } catch (e) {
      setProofModal(null);
      alert('Erro ao carregar comprovante: ' + e.message);
    }
  };

  const closeProofModal = () => {
    if (proofModal?.url) URL.revokeObjectURL(proofModal.url);
    setProofModal(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Gerenciar Inscrições
        </h2>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => setShowManualForm((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 text-white rounded-lg text-sm hover:opacity-90"
            style={{ backgroundColor: colors.primary }}
          >
            <Plus className="h-4 w-4" />
            Inscrição manual
          </button>
        </div>
      </div>

      {showManualForm && (
        <ManualForm
          onCreated={async () => {
            setShowManualForm(false);
            await loadData();
          }}
          onCancel={() => setShowManualForm(false)}
        />
      )}

      {stats && <StatsBar stats={stats} />}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex border-b">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 py-3 text-sm font-medium ${
                activeTab === key ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
              style={activeTab === key ? { backgroundColor: colors.primary } : {}}
            >
              {label} ({stats?.byStatus?.[key] ?? 0})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-8">
            <LoadingSpinner size="medium" text="Carregando..." />
          </div>
        ) : registrations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma inscrição {STATUS_LABELS[activeTab].toLowerCase()}.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {registrations.map((reg) => (
              <RegistrationRow
                key={reg.id}
                registration={reg}
                busy={actionLoading[reg.id]}
                onApprove={() => handleApprove(reg)}
                onReject={() => handleReject(reg)}
                onDelete={() => handleDelete(reg)}
                onViewProof={() => handleViewProof(reg)}
              />
            ))}
          </div>
        )}
      </div>

      {proofModal && (
        <ProofModal modal={proofModal} onClose={closeProofModal} />
      )}
    </div>
  );
}

function StatsBar({ stats }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total" value={stats.total} />
        <Stat label="Pendentes" value={stats.byStatus.pending} color="text-yellow-600" />
        <Stat label="Aprovadas" value={stats.byStatus.approved} color="text-green-600" />
        <Stat label="Rejeitadas" value={stats.byStatus.rejected} color="text-red-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 pt-4 border-t">
        {CATEGORIES.map((cat) => (
          <div key={cat} className="text-center">
            <p className="text-xs text-gray-500 capitalize">{cat}</p>
            <p className="text-lg font-bold" style={{ color: colors.primary }}>
              {stats.byCategory?.[cat]?.approved ?? 0}
            </p>
            <p className="text-xs text-gray-400">
              {(stats.byCategory?.[cat]?.pending ?? 0) > 0 &&
                `+${stats.byCategory[cat].pending} pendentes`}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, color = '' }) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function RegistrationRow({ registration, busy, onApprove, onReject, onDelete, onViewProof }) {
  const age = calculateAge(registration.birthDate);
  return (
    <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold">{registration.name}</p>
          <span
            className="text-xs px-2 py-0.5 rounded-full capitalize text-white"
            style={{ backgroundColor: colors.primary }}
          >
            {registration.category}
          </span>
          {registration.manualEntry && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              manual
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          {new Date(registration.birthDate).toLocaleDateString('pt-BR')}
          {age !== null && ` · ${age} anos`}
          {' · enviado em '}
          {new Date(registration.createdAt).toLocaleDateString('pt-BR')}
        </p>
        <p className="text-sm text-gray-600">
          Telefone: {registration.phone || '—'}
        </p>
        {registration.status === 'rejected' && registration.rejectionReason && (
          <p className="text-xs text-red-600 mt-1">
            Motivo: {registration.rejectionReason}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {registration.paymentProofPath && (
          <button
            onClick={onViewProof}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm disabled:opacity-50"
            title="Ver comprovante"
          >
            <FileText className="h-4 w-4" />
            Comprovante
          </button>
        )}
        {registration.status === 'pending' && (
          <>
            <button
              onClick={onApprove}
              disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded text-sm disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              Aprovar
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded text-sm disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              Rejeitar
            </button>
          </>
        )}
        <button
          onClick={onDelete}
          disabled={busy}
          className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
          title="Remover inscrição"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ManualForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const eligible = birthDate ? getEligibleCategories(birthDate) : [];
  const needsChoice = eligible.length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !birthDate) {
      setError('Preencha nome e data de nascimento.');
      return;
    }
    const finalCategory = needsChoice ? category : eligible[0];
    if (!finalCategory) {
      setError('Categoria inválida.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await registrationsService.createManual({
        name: name.trim(),
        birthDate,
        phone: phone.trim() || undefined,
        category: finalCategory,
      });
      if (resp.success) {
        await onCreated();
      }
    } catch (e) {
      setError(e.error?.message || 'Erro ao inscrever manualmente');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 space-y-3">
      <h3 className="font-semibold" style={{ color: colors.primary }}>
        Inscrição manual (já aprovada, sem comprovante)
      </h3>
      {error && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Nome</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nascimento</label>
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            required
            max={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Telefone</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(00) 00000-0000"
            className="w-full px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Categoria</label>
          {needsChoice ? (
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value="">Escolha...</option>
              {eligible.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border rounded text-sm">
              {birthDate
                ? eligible[0]
                  ? CATEGORY_LABELS[eligible[0]]
                  : '—'
                : 'preencha a data'}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}
        >
          {submitting ? 'Inscrevendo...' : 'Inscrever'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

function ProofModal({ modal, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex justify-between items-center" style={{ backgroundColor: colors.primary }}>
          <h3 className="text-white font-semibold">
            Comprovante - {modal.registration.name}
          </h3>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
          {modal.loading ? (
            <LoadingSpinner size="medium" text="Carregando..." />
          ) : modal.mimetype === 'application/pdf' ? (
            <div className="text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-gray-700 mb-3">
                PDF carregado.{' '}
                <a
                  href={modal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 underline"
                  style={{ color: colors.primary }}
                >
                  Abrir em nova aba
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          ) : (
            <img
              src={modal.url}
              alt="Comprovante de pix"
              className="max-w-full max-h-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
}
